import { useEffect, useMemo, useState, FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';

interface NominationDetails {
  teacherName?: string
  teacherEmail?: string
  school?: string
  nominatorName?: string
  reason?: string
  isConfirmed?: boolean
  confirmedAt?: string
  confirmedEmail?: string
  confirmedDisplayName?: string
  status?: string
}

function formatDate(value: string | null | undefined): string | null {
  if (!value) return null;
  try {
    return new Date(value).toLocaleString();
  } catch {
    return null;
  }
}

export default function TeacherNominationConfirm() {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get('token')?.trim() || '', [params]);
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState<NominationDetails | null>(null);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [alreadyConfirmed, setAlreadyConfirmed] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setError('Missing confirmation token. Check your link and try again.');
      return;
    }

    let active = true;
    setLoading(true);
    setError('');

    const CORE_API = import.meta.env.VITE_CORE_API_URL || '';
    fetch(`${CORE_API}/api/v1/nominations/verify?token=${encodeURIComponent(token)}`)
      .then(async (res) => {
        if (!active) return;
        if (!res.ok) {
          const errPayload = await res.json().catch(() => ({}));
          const errMsg = typeof errPayload?.error === 'string' ? errPayload.error : errPayload?.error?.message;
          throw new Error(errMsg || 'Unable to load nomination');
        }
        const raw = await res.json();
        const payload = raw?.data ?? raw;
        if (!active) return;
        setDetails(payload);
        setEmail(payload.confirmedEmail || payload.teacherEmail || '');
        setDisplayName(payload.confirmedDisplayName || payload.teacherName || '');
        setAlreadyConfirmed(Boolean(payload.isConfirmed));
        setSuccess(Boolean(payload.isConfirmed));
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (!active) return;
        setError((err as Error)?.message || 'Unable to load nomination');
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [token]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token || submitting) return;
    setSubmitting(true);
    setError('');

    try {
      const CORE_API = import.meta.env.VITE_CORE_API_URL || '';
      const res = await fetch(`${CORE_API}/api/v1/nominations/confirm`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ token, email, display_name: displayName }),
      });

      if (!res.ok) {
        const errPayload = await res.json().catch(() => ({}));
        const errMsg = typeof errPayload?.error === 'string' ? errPayload.error : errPayload?.error?.message;
        throw new Error(errMsg || 'Unable to confirm nomination');
      }

      const rawPayload = await res.json().catch(() => ({}));
      const payload = rawPayload?.data ?? rawPayload;
      const already = Boolean(payload?.alreadyConfirmed || payload?.already_confirmed);
      const nowIso = new Date().toISOString();
      setSuccess(true);
      setAlreadyConfirmed(already);
      setDetails((prev) => {
        if (!prev) return prev;
        const next = {
          ...prev,
          isConfirmed: true,
          status: 'confirmed',
          confirmedEmail: email,
          confirmedDisplayName: displayName,
        };
        if (!already) {
          next.confirmedAt = nowIso;
        }
        return next;
      });
    } catch (err) {
      setError((err as Error)?.message || 'Unable to confirm nomination');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-12">
      <div className="space-y-6 rounded-3xl border border-sand bg-white/90 p-8 shadow-soft">
        <header className="space-y-2">
          <h1 className="text-3xl font-semibold text-cocoa">Confirm your nomination</h1>
          <p className="text-sm text-cocoa/70">
            Join Project Tower&apos;s active teacher pool by confirming with your school or professional email.
          </p>
        </header>

        {loading ? (
          <p className="text-sm text-cocoa/70">Loading nomination details…</p>
        ) : error ? (
          <div className="rounded-xl border border-brick/40 bg-brick/10 p-4 text-sm text-brick">{error}</div>
        ) : details ? (
          <div className="space-y-6">
            <div className="rounded-2xl border border-sand bg-shell/40 p-4">
              <h2 className="text-lg font-semibold text-cocoa">{details.teacherName}</h2>
              <p className="text-sm text-cocoa/70">{details.school}</p>
              {details.nominatorName ? (
                <p className="mt-2 text-sm text-cocoa/70">Nominated by {details.nominatorName}.</p>
              ) : null}
              {details.reason ? (
                <blockquote className="mt-3 rounded-xl border border-white/60 bg-white/80 p-3 text-sm text-cocoa/80">
                  “{details.reason}”
                </blockquote>
              ) : null}
              {details.confirmedAt ? (
                <p className="mt-3 text-xs text-cocoa/60">
                  Last confirmed {formatDate(details.confirmedAt)}
                </p>
              ) : null}
            </div>

            {success ? (
              <div className="rounded-xl border border-mint/40 bg-mint/20 p-4 text-sm text-green-700">
                {alreadyConfirmed
                  ? 'You’re already confirmed! We’ll include you in upcoming Graduation Ceremonies.'
                  : 'Thanks for confirming! You are now in the active teacher pool for upcoming Graduation Ceremonies.'}
              </div>
            ) : (
              <form className="space-y-4" onSubmit={handleSubmit}>
                <div className="space-y-1">
                  <label className="text-sm font-medium text-cocoa" htmlFor="teacher-display-name">
                    How should we celebrate you?
                  </label>
                  <input
                    id="teacher-display-name"
                    className="input w-full"
                    value={displayName}
                    onChange={(event) => setDisplayName(event.target.value)}
                    placeholder="Teacher display name"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium text-cocoa" htmlFor="teacher-email">
                    School or professional email
                  </label>
                  <input
                    id="teacher-email"
                    type="email"
                    required
                    className="input w-full"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="teacher@school.edu"
                  />
                  <p className="text-xs text-cocoa/60">
                    Use your school-issued or professional email so we can confirm you for the pool.
                  </p>
                </div>
                <button disabled={submitting} className="btn btn-primary">
                  {submitting ? 'Confirming…' : 'Confirm nomination'}
                </button>
              </form>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
